import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export default function useTableState(suffix = '', { defaultSorting = [] } = {}) {
    const location = useLocation();
    const storageKey = `table_${location.pathname}${suffix ? `_${suffix}` : ''}`;
    const saved = sessionStorage.getItem(storageKey);
    const initial = saved ? JSON.parse(saved) : {};
    const [globalFilter, setGlobalFilter] = useState(initial.globalFilter ?? '');
    const [sorting, setSorting] = useState(initial.sorting ?? defaultSorting);
    const [pagination, setPagination] = useState(initial.pagination ?? { pageIndex: 0, pageSize: 25 });
    const [columnFilters, setColumnFilters] = useState(initial.columnFilters ?? []);

    // Scroll position, kept alongside the rest of the table state (user 2026-08-21).
    // Search / sorting / pagination already survived a trip into a detail page and back,
    // but the scroll did not — so returning to a long list dropped you at the top, far
    // from the row you had just been working on, and the page "was never the same again".
    const scrollKey = `${storageKey}_scroll`;
    const restored = useRef(false);

    useEffect(() => {
        const onScroll = () => sessionStorage.setItem(scrollKey, String(window.scrollY));
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            // Capture one last time on unmount: the click that navigates away can land
            // before the final scroll event is flushed.
            sessionStorage.setItem(scrollKey, String(window.scrollY));
            window.removeEventListener('scroll', onScroll);
        };
    }, [scrollKey]);

    useEffect(() => {
        if (restored.current) return;
        const target = Number(sessionStorage.getItem(scrollKey) || 0);
        if (!target) { restored.current = true; return; }
        // Rows arrive asynchronously, so the page is still short on the first frame and
        // scrolling would be clamped to the top. Retry briefly until the document is tall
        // enough, then stop — a fixed timeout would either be too early or feel sluggish.
        let tries = 0;
        const id = setInterval(() => {
            tries += 1;
            if (document.body.scrollHeight > target + window.innerHeight * 0.5) {
                window.scrollTo(0, target);
                restored.current = true;
                clearInterval(id);
            } else if (tries > 40) {          // ~2s, then give up rather than fight the user
                restored.current = true;
                clearInterval(id);
            }
        }, 50);
        return () => clearInterval(id);
    }, [scrollKey]);

    useEffect(() => {
        sessionStorage.setItem(storageKey, JSON.stringify({ globalFilter, sorting, pagination, columnFilters }));
    }, [globalFilter, sorting, pagination, columnFilters, storageKey]);

    return { globalFilter, setGlobalFilter, sorting, setSorting, pagination, setPagination, columnFilters, setColumnFilters };
}
